import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import VideoAnnotation from "core/components/VideoAnnotation";

function VideoAnnotationsOverlay({ timecodes, currentTime, paused }) {
  const [activeAnnotations, setActiveAnnotations] = useState([]);
  const manuallyClosedIds = useRef(new Set());

  useEffect(() => {
    // Фильтруем только аннотации
    const annotations =
      timecodes?.filter(
        (tc) => tc.type === "ANNOTATION" && tc.time !== undefined
      ) || [];

    if (!annotations.length) {
      setActiveAnnotations([]);
      return;
    }

    // При паузе не обновляем список (оставляем текущие аннотации)
    if (paused) {
      return;
    }

    const activeList = [];

    annotations.forEach((annotation) => {
      const showTime = annotation.time;
      const hideTime = annotation.time + (annotation.duration || 5);

      // Показываем если время в диапазоне [showTime, hideTime]
      if (currentTime >= showTime && currentTime < hideTime) {
        // И не закрыта вручную
        if (!manuallyClosedIds.current.has(annotation.id)) {
          activeList.push(annotation);
        }
      }
    });

    setActiveAnnotations(activeList);
  }, [timecodes, currentTime, paused]);

  const handleClose = (annotation) => {
    // Запоминаем что пользователь закрыл эту аннотацию
    manuallyClosedIds.current.add(annotation.id);

    // Удаляем из активных только конкретную аннотацию
    setActiveAnnotations((prev) => prev.filter((a) => a.id !== annotation.id));
  };

  if (!activeAnnotations.length) {
    return null;
  }

  // Группируем аннотации по позиции для правильного стекирования
  const annotationsByPosition = activeAnnotations.reduce((acc, annotation) => {
    const position = annotation.position || "TOP_LEFT";
    if (!acc[position]) {
      acc[position] = [];
    }
    acc[position].push(annotation);
    return acc;
  }, {});

  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 200,
      }}
    >
      {Object.entries(annotationsByPosition).map(([position, annotations]) => (
        <React.Fragment key={position}>
          {annotations.map((annotation, index) => (
            <VideoAnnotation
              key={annotation.id}
              annotation={annotation}
              onClose={() => handleClose(annotation)}
              index={index}
            />
          ))}
        </React.Fragment>
      ))}
    </Box>
  );
}

VideoAnnotationsOverlay.propTypes = {
  timecodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string,
      time: PropTypes.number,
      title: PropTypes.string,
      description: PropTypes.string,
      icon: PropTypes.string,
      duration: PropTypes.number,
      position: PropTypes.string,
    })
  ),
  currentTime: PropTypes.number.isRequired,
  paused: PropTypes.bool,
};

VideoAnnotationsOverlay.defaultProps = {
  timecodes: [],
  paused: false,
};

export default VideoAnnotationsOverlay;
